import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { GoogleLeadConversionsSection } from "@/components/GoogleLeadConversionsSection";
import { Megaphone, CalendarCheck, MapPin, X } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const fmtDay = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};
const fmtDayLong = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
};

export default function AdminCampaigns() {
  const [rows, setRows] = useState([]);
  const [cityRows, setCityRows] = useState([]);
  const [channelRows, setChannelRows] = useState([]);
  const [pixelRows, setPixelRows] = useState([]);
  const [editing, setEditing] = useState({});
  const [schedule, setSchedule] = useState(null);
  const [scheduleDays, setScheduleDays] = useState(30);
  const [scheduleCity, setScheduleCity] = useState("");

  useEffect(() => {
    const params = { days: scheduleDays };
    if (scheduleCity) params.city = scheduleCity;
    api.get("/admin/pixel-stats/schedule-daily", { params })
      .then(({ data }) => setSchedule(data))
      .catch(() => setSchedule(null));
  }, [scheduleDays, scheduleCity]);
  const byCity = schedule?.by_city || [];
  const maxCity = byCity.reduce((m, c) => Math.max(m, c.count), 0);

  const load = useCallback(async () => {
    try {
      const [c, v, ch, px] = await Promise.all([
        api.get("/admin/campaigns"),
        api.get("/admin/stats/cities"),
        api.get("/admin/profitability"),
        api.get("/admin/pixel-stats"),
      ]);
      setRows(c.data);
      setCityRows(v.data);
      setChannelRows(ch.data);
      setPixelRows(px.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSpend = async (campaign) => {
    try {
      await api.put("/admin/campaigns/spend", { campaign, amount: Number(editing[campaign] || 0) });
      toast.success("Budget enregistré");
      setEditing((e) => ({ ...e, [campaign]: undefined }));
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Suivi publicité</h1>
        <p className="mt-2 text-neutral-500">
          Chaque élève est rattaché à sa campagne via les paramètres <code>utm_campaign</code> de tes liens de pub.
          Saisis le budget dépensé pour connaître ton coût par inscription confirmée.
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          Exemple de lien à utiliser dans tes pubs : <code>{window.location.origin}/?utm_source=meta&amp;utm_medium=cpc&amp;utm_campaign=permis_b_lyon</code>
        </p>

        <section className="mt-8 rounded-lg border border-neutral-200 bg-white p-5" data-testid="meta-schedule-section">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-extrabold tracking-tight">
                <CalendarCheck className="h-5 w-5 text-brand" /> Réservations envoyées à Meta
              </h2>
              <p className="mt-1 text-sm text-neutral-500">
                {"Événement « Schedule » envoyé quand un élève a déposé ses documents obligatoires (1 réservation = 1 conversion, heure de Paris)."}
              </p>
            </div>
            <div className="flex gap-1" data-testid="meta-schedule-range">
              {[7, 30, 90].map((d) => (
                <button key={d} onClick={() => setScheduleDays(d)} data-testid={`meta-schedule-range-${d}`}
                  className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${scheduleDays === d ? "border-brand bg-brand text-white" : "border-neutral-300 bg-white hover:border-neutral-500"}`}>
                  {d} j
                </button>
              ))}
            </div>
          </div>
          {schedule && !schedule.configured && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="meta-schedule-not-configured">
              {"Pixel Meta non configuré : saisis ton ID de pixel dans Réglages → Publicité pour commencer à envoyer les réservations à Meta."}
            </p>
          )}
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              [scheduleCity ? `Aujourd'hui · ${scheduleCity}` : "Aujourd'hui", schedule?.today, "meta-schedule-today"],
              [`${scheduleDays} derniers jours${scheduleCity ? ` · ${scheduleCity}` : ""}`, schedule?.total_period, "meta-schedule-period"],
              ["Depuis le début", schedule?.total_all_time, "meta-schedule-all"],
            ].map(([label, val, tid]) => (
              <div key={tid} className="min-w-0 rounded-md bg-neutral-50 px-2.5 py-3 sm:px-3">
                <p className="text-[10px] font-semibold uppercase leading-tight tracking-wide text-neutral-500 sm:text-[11px] sm:tracking-wider">{label}</p>
                <p className="mt-1 text-2xl font-extrabold tracking-tight" data-testid={tid}>{val ?? "—"}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 h-56 w-full" data-testid="meta-schedule-chart">
            {schedule?.series?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={schedule.series} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11 }} minTickGap={12} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip labelFormatter={fmtDayLong} formatter={(v) => [v, "Réservations"]} cursor={{ fill: "rgba(0,82,255,0.06)" }} />
                  <Bar dataKey="count" fill="#0052FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="grid h-full place-items-center text-sm text-neutral-400">Chargement…</div>
            )}
          </div>

          <div className="mt-6 border-t border-neutral-100 pt-5" data-testid="meta-schedule-by-city">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-sm font-bold text-neutral-900">
                <MapPin className="h-4 w-4 text-brand" /> Par ville — {scheduleDays} derniers jours
              </h3>
              {scheduleCity && (
                <button onClick={() => setScheduleCity("")} data-testid="meta-schedule-city-reset"
                  className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1 text-xs font-bold text-white hover:bg-brand-hover">
                  {scheduleCity} <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-neutral-500">{"Clique sur une ville pour n'afficher qu'elle dans le graphique."}</p>
            {byCity.length === 0 ? (
              <p className="mt-3 text-sm text-neutral-400" data-testid="meta-schedule-city-empty">Aucune réservation envoyée à Meta sur la période.</p>
            ) : (
              <ul className="mt-3 space-y-1.5">
                {byCity.map((c, i) => {
                  const active = scheduleCity && scheduleCity.toLowerCase() === c.city.toLowerCase();
                  return (
                    <li key={c.city}>
                      <button
                        onClick={() => setScheduleCity(active ? "" : c.city)}
                        data-testid={`meta-schedule-city-${c.city}`}
                        className={`group grid w-full grid-cols-[1.5rem_minmax(0,8rem)_1fr_auto] items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors sm:grid-cols-[1.5rem_minmax(0,10rem)_1fr_auto] ${active ? "bg-brand/10 ring-1 ring-brand/40" : "hover:bg-neutral-50"}`}
                      >
                        <span className={`text-xs font-bold ${i === 0 ? "text-amber-500" : "text-neutral-400"}`}>#{i + 1}</span>
                        <span className="truncate font-semibold text-neutral-800">{c.city}</span>
                        <span className="h-2.5 overflow-hidden rounded-full bg-neutral-100">
                          <span className="block h-full rounded-full bg-brand" style={{ width: `${maxCity ? Math.max(4, (c.count / maxCity) * 100) : 0}%` }} />
                        </span>
                        <span className="whitespace-nowrap text-xs text-neutral-600">
                          <strong className="text-sm text-neutral-900">{c.count}</strong> · {c.share}%
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <GoogleLeadConversionsSection />

        <h2 className="mt-8 text-lg font-extrabold tracking-tight">Retour par pixel publicitaire</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Conversions réellement <strong>envoyées à chaque pixel</strong> (Meta, Google, TikTok) lors de la validation d&apos;un
          formulaire, et ce qu&apos;elles ont rapporté (inscrits confirmés et commissions). Configure tes identifiants de pixel
          dans <code>Réglages</code>.
        </p>
        <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="pixels-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Pixel</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Conversions envoyées</th>
                <th className="px-4 py-3">30 derniers jours</th>
                <th className="px-4 py-3">Inscrits confirmés</th>
                <th className="px-4 py-3">Commissions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {pixelRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-neutral-500" data-testid="no-pixels">
                    Aucune conversion pixel enregistrée pour l&apos;instant.
                  </td>
                </tr>
              )}
              {pixelRows.map((r) => (
                <tr key={r.pixel} data-testid={`pixel-row-${r.pixel}`} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-semibold">{r.label}</td>
                  <td className="px-4 py-3">
                    {r.configured ? (
                      <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`pixel-status-${r.pixel}`}>
                        Actif
                      </span>
                    ) : (
                      <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-500" data-testid={`pixel-status-${r.pixel}`}>
                        Non configuré
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-bold" data-testid={`pixel-sent-${r.pixel}`}>{r.conversions_sent}</td>
                  <td className="px-4 py-3">{r.conversions_30d}</td>
                  <td className="px-4 py-3">{r.confirmed}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-700">{r.commissions} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-neutral-400">
          Une « conversion envoyée » est comptée quand le pixel s&apos;est déclenché dans le navigateur du candidat au moment de
          l&apos;envoi du formulaire. Les bloqueurs de publicité peuvent réduire ce nombre côté plateforme.
        </p>

        <h2 className="mt-12 text-lg font-extrabold tracking-tight">Rentabilité par canal d'acquisition</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Vue synthétique par source (<code>utm_source</code>) : ton CAC réel, tes commissions encaissées et ton ROI.
          Coupe les canaux au ROI négatif, mets le budget sur ceux qui rapportent.
        </p>
        <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="channels-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Canal</th>
                <th className="px-4 py-3">Leads</th>
                <th className="px-4 py-3">Confirmés</th>
                <th className="px-4 py-3">Taux</th>
                <th className="px-4 py-3">Budget pub</th>
                <th className="px-4 py-3">CAC</th>
                <th className="px-4 py-3">Encaissé</th>
                <th className="px-4 py-3">Marge</th>
                <th className="px-4 py-3">ROI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {channelRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-neutral-500" data-testid="no-channels">
                    Aucune donnée de canal pour l'instant.
                  </td>
                </tr>
              )}
              {channelRows.map((r) => (
                <tr key={r.channel} data-testid={`channel-row-${r.channel}`} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-semibold">{r.channel}</td>
                  <td className="px-4 py-3 font-semibold">{r.leads}</td>
                  <td className="px-4 py-3">{r.confirmed}</td>
                  <td className="px-4 py-3">{r.conversion_rate} %</td>
                  <td className="px-4 py-3">{r.spend ? `${r.spend} €` : "—"}</td>
                  <td className="px-4 py-3 font-semibold" data-testid={`channel-cac-${r.channel}`}>
                    {r.cac ? `${r.cac} €` : "—"}
                  </td>
                  <td className="px-4 py-3">{r.commissions_facturees} €</td>
                  <td className={`px-4 py-3 font-bold ${r.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.margin} €
                  </td>
                  <td className="px-4 py-3 font-bold">
                    {r.roi === null || r.roi === undefined ? (
                      <span className="text-neutral-400">—</span>
                    ) : (
                      <span
                        data-testid={`channel-roi-${r.channel}`}
                        className={`rounded-md px-2 py-0.5 text-xs ${
                          r.roi >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                        }`}
                      >
                        {r.roi} %
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-12 text-lg font-extrabold tracking-tight">Détail par campagne</h2>
        <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Campagne</th>
                <th className="px-4 py-3">Source / Canal</th>
                <th className="px-4 py-3">Leads</th>
                <th className="px-4 py-3">Attribués</th>
                <th className="px-4 py-3">Confirmés</th>
                <th className="px-4 py-3">Taux</th>
                <th className="px-4 py-3">Budget pub</th>
                <th className="px-4 py-3">Coût / lead</th>
                <th className="px-4 py-3">Coût / inscription</th>
                <th className="px-4 py-3">Commissions</th>
                <th className="px-4 py-3">Marge</th>
                <th className="px-4 py-3">Encaissé</th>
                <th className="px-4 py-3">ROI</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={13} className="px-4 py-8 text-neutral-500" data-testid="no-campaigns">
                    Aucun lead pour l'instant.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.campaign} data-testid={`campaign-row-${r.campaign}`} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      <Megaphone className="h-3.5 w-3.5 text-brand" />
                      {r.campaign}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {r.source} / {r.medium}
                  </td>
                  <td className="px-4 py-3 font-semibold">{r.leads}</td>
                  <td className="px-4 py-3">{r.assigned}</td>
                  <td className="px-4 py-3">{r.confirmed}</td>
                  <td className="px-4 py-3">{r.conversion_rate} %</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <input
                        data-testid={`spend-input-${r.campaign}`}
                        type="number"
                        value={editing[r.campaign] ?? r.spend}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.campaign]: e.target.value }))}
                        className="w-20 rounded border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-brand transition-colors"
                      />
                      <button
                        data-testid={`save-spend-${r.campaign}`}
                        onClick={() => saveSpend(r.campaign)}
                        className="rounded bg-neutral-900 px-2 py-1 text-xs font-bold text-white transition-colors hover:bg-brand"
                      >
                        OK
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">{r.cost_per_lead ? `${r.cost_per_lead} €` : "—"}</td>
                  <td className="px-4 py-3 font-semibold" data-testid={`cpa-${r.campaign}`}>
                    {r.cost_per_confirmed ? `${r.cost_per_confirmed} €` : "—"}
                  </td>
                  <td className="px-4 py-3">{r.commissions} €</td>
                  <td className={`px-4 py-3 font-bold ${r.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.margin} €
                  </td>
                  <td className="px-4 py-3">{r.commissions_facturees ?? 0} €</td>
                  <td className="px-4 py-3 font-bold" data-testid={`roi-${r.campaign}`}>
                    {r.roi === null || r.roi === undefined ? (
                      <span className="text-neutral-400">—</span>
                    ) : (
                      <span className={r.roi >= 0 ? "text-emerald-700" : "text-red-600"}>{r.roi} %</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <h2 className="mt-12 text-lg font-extrabold tracking-tight">Performance par page ville</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Résultats de chaque page locale utilisée dans tes pubs (ex. /permis/boulogne-billancourt).
        </p>
        <div className="mt-4 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="cities-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Page</th>
                <th className="px-4 py-3">Villes des élèves</th>
                <th className="px-4 py-3">Leads</th>
                <th className="px-4 py-3">Attribués</th>
                <th className="px-4 py-3">Confirmés</th>
                <th className="px-4 py-3">Taux</th>
                <th className="px-4 py-3">Commissions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {cityRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-neutral-500" data-testid="no-city-stats">
                    Aucune donnée.
                  </td>
                </tr>
              )}
              {cityRows.map((r) => (
                <tr key={r.page} data-testid={`city-row-${r.page}`} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-semibold">{r.page}</td>
                  <td className="px-4 py-3 text-xs text-neutral-600">{r.cities.join(", ") || "—"}</td>
                  <td className="px-4 py-3 font-semibold">{r.leads}</td>
                  <td className="px-4 py-3">{r.assigned}</td>
                  <td className="px-4 py-3">{r.confirmed}</td>
                  <td className="px-4 py-3">{r.conversion_rate} %</td>
                  <td className="px-4 py-3">{r.commissions} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
